import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState({ balance: 0, unlimited: false });
  const [loading, setLoading] = useState(true);

  const refreshBalance = useCallback(async () => {
    try {
      const { data } = await api.get("/credits/balance");
      setBalance(data);
    } catch (e) { /* balance refresh — non-critical */ }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("co_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        return refreshBalance();
      })
      .catch(() => localStorage.removeItem("co_token"))
      .finally(() => setLoading(false));
  }, [refreshBalance]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("co_token", data.token);
    setUser(data.user);
    setBalance({ balance: data.user.credits_balance || 0, unlimited: data.user.role === "owner" });
    await refreshBalance();
    return data.user;
  };

  const signup = async ({ email, password, name, referralCode }) => {
    const { data } = await api.post("/auth/signup", {
      email, password, name,
      referral_code: referralCode || undefined,
    });
    localStorage.setItem("co_token", data.token);
    setUser(data.user);
    setBalance({ balance: data.user.credits_balance || 0, unlimited: false });
    await refreshBalance();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("co_token");
    setUser(null);
    window.location.href = "/login";
  };

  const isOwner = user?.role === "owner";

  return (
    <AuthContext.Provider value={{ user, balance, loading, login, signup, logout, refreshBalance, refreshUser, isOwner }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
